import React, { useState } from 'react';
import { FlatList, Image } from "react-native"
import type { AthleteProps, CompetitionMatchMarketProps, CompetitionMatchProps, CompetitionProps, EventProps, LeagueProps, MarketProps, MatchProps, MyPlayerProps, TeamProps, TournamentProps } from '../../types';
import { Icons, Switch } from '../../Components';
import CompetitionMatchMarketCard from './CompetitionMatchMarketCard';
import moment from 'moment-mini';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import { showConfirmAlert } from '../../Components/ConfirmAlert';


type CompetitionContestsFormProps = {
    action_loading:boolean,
    competition:CompetitionProps,
    player?:MyPlayerProps,
    markets:MarketProps[],
    leagues:LeagueProps[],
    primary_markets:{ market:MarketProps, included:boolean}[],
    events:EventProps[],
    tournaments:TournamentProps[],
    matches:MatchProps[],
    athletes:AthleteProps[],
    teams:TeamProps[],
    competition_matches: CompetitionMatchProps[],
    competition_match_markets:CompetitionMatchMarketProps[],
    onShowContests:() => void,
    onAddPrimaryMarket:(market:MarketProps) => void,
    onRemovePrimaryMarket:(market:MarketProps) => void,
    onShowAthletes:(contest_id:string, contest_type:string, market_id:string) => void,
    onShowMarkets:(contest_id:string, contest_type:string) => void, 
    onDeleteMarket:(contest_id:string, contest_type:string, market:MarketProps) => void,
    onSaveCompetitionMatch:(competition_id:string, event_id:string, event_type:string, match_type:string, event:EventProps, market_id_override?:string, side_type_override?:string, side_id_override?:string) => void,
    onUpdateCompetitionMatch:(cm:CompetitionMatchProps) => void,
    onSaveCompetitionMatchMarkets: (cmms:CompetitionMatchMarketProps[]) => void,
    onDeleteCompetitionMatchMarkets:(cmms:CompetitionMatchMarketProps[]) => void,
    onDeleteCompetitionMatch: (competition_match_id:string) => void,
    onUpdateCompetition:(competition:CompetitionProps) => void
}


const CompetitionContestsForm = ({
    competition,
    action_loading,
    primary_markets,
    events,
    athletes,
    markets,
    player,
    leagues,
    competition_matches,
    competition_match_markets,
    onDeleteMarket,
    onRemovePrimaryMarket,
    onDeleteCompetitionMatch,
    onDeleteCompetitionMatchMarkets,
    onShowAthletes,
    onShowContests,
    onShowMarkets,
    onAddPrimaryMarket,
    onUpdateCompetition
}:CompetitionContestsFormProps) => {
    const Colors = useColors();
    const [ expanded, setExpanded ] = useState(true);
    const [ show_match_markets, setShowMatchMarkets ] = useState<string[]>([]);
    const [ active_contest_type ] = useState('team');
    const [ expanded_contests, setExpandedContests ] = useState<string[]>([]);

    const unique_team_events = [ ...new Set(competition_matches.filter(cm => ['inprogress', 'pending'].includes(cm.status) && cm.event_type == 'team').map(cm => cm.event_id)) ]
    const append_to_events = competition.linked_event_ids && competition.linked_event_ids.length == unique_team_events.length ? true : false 
    const renderPrimaryMarkets = (data: { item:{ market:MarketProps, included:boolean }, index:number }) => {
        return (
            <View float style={{ padding:10, width:100, borderRadius:8,  margin:5 }}>
                <Text style={{ marginBottom:10 }} theme='h1'>{data.item.market.type}</Text>
                <Switch
                    switch_type='on_off'
                    value={data.item.included}
                    onChange={(val) => val ? onAddPrimaryMarket(data.item.market) : onRemovePrimaryMarket(data.item.market)}
                />
            </View>
        )
    }
    
    const renderMarkets = (data: { item:string, index:number, event_id:string, event_type:string, visible_matches:CompetitionMatchProps[] }) => {
        const market = markets.find(m => m.market_id == data.item);
        if(!market){ return <></> }
        const show_details = show_match_markets.includes(`${data.event_type}:${data.event_id}:${data.item}`) ? true : false
        const market_matches = competition_matches.filter(cm => cm.market_id_override == data.item && cm.side_id_override);
        return (
            <View style={{ padding:0, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Button transparent style={{ flexDirection:'row', alignItems:'center', padding:10 }} onPress={() => {
                            if(show_details){ return setShowMatchMarkets(show_match_markets.filter(smm => smm != `${data.event_type}:${data.event_id}:${data.item}`)) }
                            return setShowMatchMarkets(show_match_markets.concat(`${data.event_type}:${data.event_id}:${data.item}`))
                        }}>
                    <Button
                        title='X'
                        loading={action_loading}
                        style={{padding:10}}
                        transparent
                        title_color={Colors.text.error}
                        onPress={() => {
                            showConfirmAlert('Are you sure?', 'This will remove any athlete / team props associated with this market as well.', 
                                () => onDeleteMarket(data.event_id, data.event_type, market),
                                () => console.log('')
                            )
                        }}
                    />
                    <View transparent style={{ flex:1 }}>
                        <Text theme='h1'>{market.stat_label ?? market.type}</Text>
                        <Text style={{ marginTop:3 }} theme='description'>{market.description}</Text>
                        {market.level != 'event' && market_matches.length == 0 ?
                        <Text style={{ marginTop:3 }} theme='warning'>Please add at least 1 {market.level}</Text>
                        :<></>}
                    </View>
                    <View transparent style={{ flexDirection:'row', alignItems:'center', padding:0 }}>
                        {market.level != 'event' ?
                        <View transparent style={{ margin:5, justifyContent:'center', alignItems:'center' }}>
                            <Text theme='h2' size={14} color={Colors.text.action}>{market_matches.length}</Text>
                            <Text theme='description' color={Colors.text.action} style={{ marginTop:3 }}>{market.level.toUpperCase()}{market_matches.length == 1 ? '':'S'}</Text>
                        </View>
                        :
                        <View transparent>
                            <Icons.ChevronIcon direction={show_details ? 'up' : 'down'} color={Colors.text.action} size={8} />
                        </View>
                        }
                    </View>
                </Button>
                {market.level == 'athlete' && show_details ?
                <Button
                    title="ADD ATHLETE"
                    style={{ padding:10 }}
                    type='success'
                    onPress={() => {
                        onShowAthletes(data.event_id, data.event_type, data.item);
                    }}
                />
                :<></>}
                {data.visible_matches.length > 0 && show_details ?
                    <FlatList
                        data={data.visible_matches}
                        renderItem={renderCompetitionMatches}
                        keyExtractor={(item) => item.competition_match_id.toString()}
                    />
                :<></>}
            </View>
                
        )
    }

    
    const showDetails = (show_event_id:string, show_event_type:string) => {
        
        let show_cms:CompetitionMatchProps[] = []
        show_match_markets.map(smm => {
            let split_smm = smm.split(':')
            let event_type = split_smm[0]
            let event_id = split_smm[1]
            let market_id = split_smm[2]
            if(event_id != show_event_id){ return }
            if(show_event_type != event_type){ return }
            const market = markets.find(m => m.market_id == market_id);
            if(!market){ return }
            if(market.level == 'event' && market.primary_market){
                const primary_cm = competition_matches.find(cm => cm.event_id == event_id && cm.event_type == event_type && !cm.market_id_override);
                if(!primary_cm){ return }
                const primary_cmms = competition_match_markets.filter(cmm => cmm.competition_match_id == primary_cm?.competition_match_id && cmm.market_id == market_id);
                show_cms.push({ ...primary_cm, competition_match_markets:primary_cmms })
            }
            if(market.level == 'athlete'){
                const cms = competition_matches.filter(cm => cm.event_id == event_id && cm.event_type == event_type && cm.market_id_override == market_id)
                cms.map(cm => {
                    let cmm = competition_match_markets.filter(cmm => cmm.competition_match_id == cm.competition_match_id);
                    //if(cmm.length == 2){
                        show_cms.push({ ...cm, competition_match_markets: cmm })
                    //}
                })
            }
        })
        return show_cms
    }
       

    const getMatchTitle = (cm:CompetitionMatchProps, market:MarketProps):{image_url?:string, header:string, description:string} => {
        
        let error = { header: 'Market', description:market.description }
        switch(cm.event_type){
            case 'team':
                const event = events.find(e => e.event_id == cm.event_id)
                if(!event){ return error }
                if(!cm.market_id_override){
                    //This is a primary one that we can just do the market name and event title
                    return { header: event.event_title, description: market.description }
                }
                switch(cm.side_type_override){
                    case 'athlete':
                        const athlete = athletes.find(a => a.athlete_id == cm.side_id_override)
                        if(!athlete){ return error }
                        return { image_url:athlete.image?.url, header: `${athlete.first_name} ${athlete.last_name} ${market.stat_label}`, description:market.description }
                    default: return error
                }
            default: return error
        }
        
    }
    

    const renderCompetitionMatches = (data: { item:CompetitionMatchProps, index:number }) => {
        if(!data.item.competition_match_markets){ return <></> }
        const market = markets.find(m => data.item.competition_match_markets && m.market_id == data.item.competition_match_markets[0]?.market_id)
        if(!market){ return <></> }

        const { image_url, header, description } = getMatchTitle(data.item, market)
        return (
            <View transparent style={{ padding:10, flexDirection:'row', alignItems:'center', flexWrap:'wrap', borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Button
                    title="X"
                    style={{ marginRight:5, padding:10 }}
                    type='error'
                    onPress={() => {
                        if(data.item.market_id_override){
                            return onDeleteCompetitionMatch(data.item.competition_match_id)
                        }
                        return onDeleteCompetitionMatchMarkets(data.item.competition_match_markets ?? [])
                    }}
                />
                {image_url ?
                <View transparent style={{ padding:5 }}>
                    <Image
                        source={{ uri:image_url }}
                        style={{ marginRight:10, height:40, width:40, borderRadius:100 }}
                        resizeMode="center"
                    />
                </View>
                :<></>}
                <View transparent style={{ flexGrow:1, marginLeft:10, marginRight:5, minWidth:200 }}>
                    <Text size={14} weight='bold'>{header}</Text>
                    <Text style={{ marginTop:3 }} size={12} weight='bold'>{description}</Text>
                </View>
                <CompetitionMatchMarketCard
                    event_id={data.item.event_id}
                    event_type={data.item.event_type}
                    competition_match_id={data.item.competition_match_id}
                    competition_match_markets={data.item.competition_match_markets}
                    competition_summaries={[]}
                    market={market}
                    onSaveMarkets={(cmms) => console.log(cmms)}
                    onUpdateMarkets={(cmms) => console.log(cmms)}
                    onGenerateMarkets={() => console.log('Blah')}
                />
            </View>
        )
    }
        
    const renderTeamEvents = (data: { item:string, index:number }) => {
        const event = events.find(e => e.event_id == data.item);
        if(!event){ return <></> }
        if(!event.supported_markets){ return <></> }
        const cms = competition_matches.filter(cm => cm.event_id == data.item && cm.event_type == 'team');
        const cmms = competition_match_markets.filter(cmm => cms.map(cm => cm.competition_match_id.toString()).includes(cmm.competition_match_id));
        const unique_market_ids = [ ...new Set(cmms.map(cmm => cmm.market_id).concat(cms.filter(cm => cm.market_id_override).map(cm => cm.market_id_override ?? '0'))) ]
        const league = leagues.find(l => l.league_id == event.league_id);
        const visible_matches = showDetails(data.item, 'team')
        const expanded = expanded_contests.includes(`${event.event_id}:team`) ? true : false
        return (
            <View float style={{ margin:8 }}>
                <Button style={{ flexDirection:'row', alignItems:'center', padding:15, zIndex:1}} onPress={() => {
                    if(expanded){ return setExpandedContests(expanded_contests.filter(c => c != `${event.event_id}:team`)) }
                    else { return setExpandedContests(expanded_contests.concat(`${event.event_id}:team`)) }
                }}>
                    {['pending','paused'].includes(competition.status) && cms.length == 1 && cmms.length == 0 ? 
                    <Button
                        title="X"
                        type='error'
                        onPress={() => onDeleteCompetitionMatch(cms[0]?.competition_match_id ?? '')}
                    />
                    :<></>}
                    <View transparent style={{ flex:1, marginLeft:5, flexDirection:'row', alignItems:'center' }}>
                        {league?.league_image ?
                        <Image
                            source={{ uri: league.league_image }}
                            style={{ height:30, width:30, marginRight:10 }}
                            resizeMode="cover"
                        />
                        :<></>}
                        <View transparent style={{ flex:1 }}>
                            <Text size={14} theme='h1'>{event.away?.market_name} {event.away?.name} vs {event.home?.market_name} {event.home?.name}</Text>
                            <Text style={{ marginTop:4 }} size={12} theme='description'>{moment(event.scheduled_datetime).format('MMMM DD hh:mm a')}</Text>
                        </View>
                    </View>
                    <View transparent style={{ marginLeft:5, marginRight:5, height:25, width:25, justifyContent:'center', alignItems:'center', borderRadius:100 }}>
                        <Text size={10} color={Colors.text.h1} weight='bold'>{unique_market_ids.length}</Text>
                    </View>
                    <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} size={8} color={Colors.text.h1} />
                </Button>
                {expanded ?
                <View transparent>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <View transparent style={{ flex:1, marginRight:10 }}>
                            <Text theme='h1'>Selected Markets</Text>
                            <Text theme='description'>Select Add Markets to manage the markets for this contest</Text>
                        </View>
                        <Button
                            title="Add Markets"
                            style={{ padding:10 }}
                            type='action'
                            onPress={() => {
                                onShowMarkets(data.item, 'team');
                            }}
                        />
                    </View>
                    <View transparent style={{ padding:10 }}>
                        {unique_market_ids.map((market_id, index) => {
                            return (
                                renderMarkets({ item: market_id, index, event_id:data.item, event_type:'team', visible_matches:visible_matches })
                            )
                        })}
                    </View>
                </View>

                :<></>}
                
            </View>
        )
    }

    return (
        <View float style={{ borderRadius:0 }}>
            <Button float style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:20, backgroundColor:Colors.toggle.button.active }} onPress={() => setExpanded(!expanded)}>
                <View transparent style={{ flex:1, marginRight:10, marginLeft:10 }}>
                    <Text theme='h1' color={Colors.toggle.text.active}>Competition Contests</Text>
                    <Text style={{ marginTop:3 }} theme='description' color={Colors.toggle.text.active}>Competitions require contests for users to make picks / wagers.  Select 'Add Contests' to get started.</Text>
                </View>
                <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} size={8} color={Colors.text.h1} />
            </Button>
            {player?.role == 'admin' ?
            <View type='row' style={{  padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View style={{ flex:1 }}>
                    <Text theme='h1'>Append To Events</Text>
                    <Text style={{ marginTop:3 }} theme='description'>Should the competition show up on events that are included in this competition?</Text>
                </View>
                <Switch 
                    value={append_to_events}
                    onChange={(val) => {
                        if(val){ return onUpdateCompetition({ ...competition, linked_event_type: 'team', linked_event_ids: unique_team_events }) }
                        return onUpdateCompetition({ ...competition, linked_event_type: 'team', linked_event_ids: undefined }) 
                    }}
                    switch_type='on_off'
                />
            </View>
            :<></>}
            {expanded ?
            <View type='body' style={{ padding:0 }}>
                <View style={{flexDirection:'row', alignItems:'center', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light}}>
                    <Button style={{ flex:1, padding:0 }}>
                        <Text theme='h1'>Team Events</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Select team based events to include in this competition</Text>
                    </Button>
                    <Button
                        title='ADD'
                        type='success'
                        onPress={() => onShowContests()}
                    />
                </View>
                <View transparent style={{ padding:20 }}>
                    <View transparent>
                        <Text theme='h1'>Primary Markets</Text>
                        <Text theme='description' style={{ marginTop:3 }}>The following markets can be added to all events</Text>
                    </View>
                    <View transparent style={{ marginTop:10 }}>
                        <FlatList
                            data={primary_markets}
                            renderItem={renderPrimaryMarkets}
                            horizontal
                            keyExtractor={(item) => item.market.market_id.toString()}
                        />
                    </View>
                </View>
                <View type='body' style={{ padding:0 }}>
                    {active_contest_type == 'team' ?
                    <View transparent>
                        {unique_team_events.map((id, index) => {
                            return renderTeamEvents({ item:id, index })
                        })}
                    </View>
                    :<></>}
                </View>
            </View>
            :<></>}
        </View>
    )
        
}

export default CompetitionContestsForm